import React from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { useMemo } from 'react';
import { useUiStore } from './stores/ui.store';
import { useTenantStore } from './stores/tenant.store';
import { AuthProvider } from './contexts/auth.context';
import { ConfirmProvider } from './components/ConfirmProvider';
import { App } from './App';

// i18n — must be imported before rendering
import './i18n';
import { installZodErrorMap } from './i18n/zod-error-map';
import { assertWebEnvironment } from './lib/environment';
import { portalViewGoneOf } from './lib/portal-view';

// A build without its Firebase project must not start: it would talk to
// whichever project a fallback named.
assertWebEnvironment();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // «Ver portal»: si el servidor dice que la vista ya no sirve (venció, no
      // vale, o la persona figura ahora como fallecida), reintentar no la
      // revive — la página lo dice de una vez (lib/portal-view). Lo demás, los
      // mismos 3 reintentos de siempre.
      retry: (failureCount, error) => portalViewGoneOf(error) === null && failureCount < 3,
    },
  },
});

// UN ROUTER DE DATOS con una sola ruta comodín que envuelve la app de siempre
// (sus <Routes> no cambian). Se pasó de <BrowserRouter> por una razón: el
// bloqueo de navegación (`useBlocker`) solo existe en un router de datos, y es
// lo que permite avisar «hay notas sin guardar» antes de ir a otra pantalla
// —incluido el botón Atrás— (UnsavedChangesGuard).
const router = createBrowserRouter([
  {
    path: '*',
    element: (
      <AuthProvider>
        <ConfirmProvider>
          <App />
        </ConfirmProvider>
      </AuthProvider>
    ),
  },
]);

function ThemedApp() {
  const { colorMode } = useUiStore();
  const { currentTenantTheme } = useTenantStore();

  const primaryColor = currentTenantTheme?.primaryColor ?? '#6366f1';

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: colorMode,
          primary: { main: primaryColor },
          secondary: { main: '#8b5cf6' },
          background:
            colorMode === 'dark'
              ? { default: '#0f172a', paper: '#1e293b' }
              : { default: '#f1f5f9', paper: '#ffffff' },
        },
        typography: {
          fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        },
        shape: { borderRadius: 10 },
        components: {
          MuiButton: {
            defaultProps: { disableElevation: true },
            styleOverrides: { root: { textTransform: 'none', fontWeight: 600 } },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
                border:
                  colorMode === 'dark'
                    ? '1px solid rgba(255,255,255,0.07)'
                    : '1px solid rgba(0,0,0,0.08)',
              },
            },
          },
          MuiTableCell: {
            styleOverrides: {
              root: {
                borderColor: colorMode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)',
              },
            },
          },
        },
      }),
    [colorMode, primaryColor],
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <RouterProvider router={router} />
    </ThemeProvider>
  );
}

// Mensajes de validación traducidos, ANTES de montar: cualquier schema que se
// evalúe durante el primer render ya encuentra el mapa instalado.
installZodErrorMap();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemedApp />
    </QueryClientProvider>
  </React.StrictMode>,
);
